import { ThemeOptions, createTheme } from '@mui/material/styles';
// Teaches the theme about MuiDataGrid, so the grid can be styled once here
// instead of with a repeated sx block on every list screen.
import type {} from '@mui/x-data-grid/themeAugmentation';
import { ThemeMode, getColors } from './colors';

/**
 * One theme for the whole app, in both modes.
 *
 * <p>The retail suite re-declares the same `sx` block on every page - the DataGrid
 * header, the outlined fields, the small contained buttons. Here those live once, as
 * component defaults, so a new screen looks right without copying anything.
 */
export const buildTheme = (mode: ThemeMode) => {
  const colors = getColors(mode);

  const options: ThemeOptions = {
    palette: {
      mode,
      primary: colors.primary,
      secondary: colors.secondary,
      error: colors.error,
      warning: colors.warning,
      info: colors.info,
      success: colors.success,
      text: colors.text,
      background: { default: colors.background.default, paper: colors.background.paper },
      divider: colors.divider,
    },

    shape: { borderRadius: 8 },

    typography: {
      fontFamily: 'Roboto, system-ui, -apple-system, "Segoe UI", Arial, sans-serif',
      h1: { fontWeight: 700, fontSize: '2.5rem', lineHeight: 1.2 },
      h2: { fontWeight: 700, fontSize: '2rem', lineHeight: 1.25 },
      h3: { fontWeight: 700, fontSize: '1.75rem', lineHeight: 1.3 },
      h4: { fontWeight: 700, fontSize: '1.5rem', lineHeight: 1.4 },
      // Every page title in the app is an h5 in capitals, as in the retail suite.
      h5: { fontWeight: 600, fontSize: '1.125rem', lineHeight: 1.5, letterSpacing: '.02em' },
      h6: { fontWeight: 600, fontSize: '1rem', lineHeight: 1.6 },
      body1: { fontSize: '1rem', lineHeight: 1.6 },
      body2: { fontSize: '0.875rem', lineHeight: 1.7 },
      subtitle1: { fontWeight: 500, fontSize: '1rem', lineHeight: 1.6 },
      subtitle2: { fontWeight: 400, fontSize: '0.875rem', lineHeight: 1.7 },
      caption: { fontWeight: 400, fontSize: '0.75rem', lineHeight: 1.7 },
      button: { textTransform: 'none', fontWeight: 500 },
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          /*
           * Tells the browser which way round the page is, so it draws the controls it
           * owns to match - the date box, the calendar that drops out of it, the native
           * dropdown on a select.
           *
           * Without it those stay light whatever the theme says, because the browser
           * paints them and not us. In dark mode that left the From and To dates all
           * but unreadable: pale text on a pale field, with a black calendar icon.
           */
          ':root': { colorScheme: mode },
          body: { backgroundColor: colors.background.default },
          '::-webkit-scrollbar': { width: 8, height: 8, backgroundColor: 'transparent' },
          '::-webkit-scrollbar-thumb': { borderRadius: 10, backgroundColor: colors.divider },
          // The keyboard-first billing screen marks its focused element with this.
          '.kb-focus:focus-visible': {
            outline: `2px solid ${colors.focusRing}`,
            outlineOffset: 2,
            borderRadius: 8,
          },
          'input[type=number]::-webkit-outer-spin-button, input[type=number]::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
          },
          'input[type=number]': { MozAppearance: 'textfield' },
        },
      },

      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
        },
      },

      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { border: `1px solid ${colors.divider}`, borderRadius: 12 },
        },
      },

      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 8 },
          sizeSmall: { paddingInline: 14 },
        },
      },

      MuiTextField: {
        defaultProps: { variant: 'outlined', size: 'small' },
      },

      MuiChip: {
        styleOverrides: { sizeSmall: { fontSize: '0.75rem' } },
      },

      MuiTooltip: {
        styleOverrides: {
          tooltip: { fontSize: '0.75rem' },
        },
      },

      MuiTableCell: {
        styleOverrides: {
          head: { fontWeight: 700, backgroundColor: mode === 'dark' ? 'rgba(255,255,255,.04)' : 'rgba(0,0,0,.03)' },
          sizeSmall: { padding: '6px 10px' },
        },
      },

      MuiDataGrid: {
        styleOverrides: {
          root: {
            border: `1px solid ${colors.divider}`,
            borderRadius: 12,
            backgroundColor: colors.background.paper,
          },
          // The retail suite's grid header, kept identical so the two apps match.
          columnHeader: {
            fontWeight: 'bold',
            fontSize: '15px',
            backgroundColor: mode === 'dark' ? 'rgba(255,255,255,.04)' : 'rgba(0, 0, 0, 0.03)',
          },
          columnHeaderTitle: { fontWeight: 700 },
          cell: { borderColor: colors.divider },
        },
      },
    },
  };

  return createTheme(options);
};
